import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "../../../../ui/components/Button";
import { Loader } from "../../../../ui/components/Loader";
import { Tabs } from "../../../../ui/components/Tabs";
import { TextField } from "../../../../ui/components/TextField";
import { ReviewRequestModal } from "../../../../shared/presentation/components/ReviewRequestModal";
import { useToast } from "../../../../shared/presentation/components";
import { usePermissions } from "../../../../shared/presentation/hooks/usePermissions";
import {
  AccountingAreaSidebar,
  TaskGuidancePanel,
  PendingChecklistItemCard,
  ApprovedChecklistItemCard,
  SentBackChecklistItemCard,
} from "../components";
import {
  useChecklistCounts,
  useChecklistItems,
  useChecklistProgress,
} from "../hooks";
import {
  MarkItemDoneUseCase,
  MarkItemToBeDoneLaterUseCase,
  UploadAttachmentUseCase,
  CloseMonthUseCase,
  TriggerCronClientMonthUseCase,
} from "../../domain";
import {
  MONTH_END_CHECKLIST_ACCOUNTING_AREAS,
  MONTH_END_CHECKLIST_ALL_AREAS_KEY,
} from "../../../../shared/constants/monthEndChecklist";
import { DeleteAttachmentUseCase } from "../../domain/use-cases/checklist/DeleteAttachmentUseCase";
import { ChecklistRepositoryImpl } from "../../data/repositories/ChecklistRepositoryImpl";
import {
  ChecklistItemEntity,
  ChecklistItemsTabStatus,
} from "../../domain/entities/ChecklistEntity";
import { ChangeDetails } from "../../../../shared/utils/reviewWorkflow";

// Create repository and use cases
const checklistRepository = new ChecklistRepositoryImpl();
const markItemDoneUseCase = new MarkItemDoneUseCase(checklistRepository);
const markItemToBeDoneLaterUseCase = new MarkItemToBeDoneLaterUseCase(
  checklistRepository,
);
const uploadAttachmentUseCase = new UploadAttachmentUseCase(
  checklistRepository,
);
const deleteAttachmentUseCase = new DeleteAttachmentUseCase(
  checklistRepository,
);
const closeMonthUseCase = new CloseMonthUseCase(checklistRepository);
const triggerCronClientMonthUseCase = new TriggerCronClientMonthUseCase(
  checklistRepository,
);

const ACCOUNTING_AREAS = MONTH_END_CHECKLIST_ACCOUNTING_AREAS;
type ChecklistReviewAction = "mark_as_done" | "to_be_done_later";

export const ChecklistPage: React.FC = () => {
  const [selectedArea, setSelectedArea] = useState<string | null>(
    ACCOUNTING_AREAS[0], // Auto-select first area by default
  );
  const [status, setStatus] = useState<ChecklistItemsTabStatus | null>(
    "pending",
  );
  const [selectedItem, setSelectedItem] = useState<ChecklistItemEntity | null>(
    null,
  );
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewItem, setReviewItem] = useState<ChecklistItemEntity | null>(
    null,
  );
  const [reviewAction, setReviewAction] =
    useState<ChecklistReviewAction>("mark_as_done");
  const [reviewNotes, setReviewNotes] = useState<string>("");
  const [itemSearchInput, setItemSearchInput] = useState("");
  const [debouncedItemSearch, setDebouncedItemSearch] = useState("");

  // Get current month in YYYY-MM format
  const defaultMonth = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  }, []);

  const [selectedMonth, setSelectedMonth] = useState<string>(defaultMonth);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedItemSearch(itemSearchInput.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [itemSearchInput]);

  useEffect(() => {
    setItemSearchInput("");
    setDebouncedItemSearch("");
  }, [selectedMonth, selectedArea]);

  // Generate list of months (last 12 months)
  const months = useMemo(() => {
    const monthList: { value: string; label: string; shortLabel: string }[] =
      [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthYear = `${date.getFullYear()}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}`;
      const monthName = date.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
      const shortLabel = date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
      monthList.push({ value: monthYear, label: monthName, shortLabel });
    }
    return monthList;
  }, []);

  const { showSuccess, showError } = useToast();
  const { getPermissionInfo, loading: permissionsLoading } = usePermissions();

  // Check permissions - always use monthly_checklist
  // Wait for permissions to load before checking
  const markAsDonePermissionInfo = permissionsLoading
    ? { granted: false, requiresReview: false }
    : getPermissionInfo("monthly_checklist", "mark_as_done");

  const hasCompleteTaskPermission = markAsDonePermissionInfo.granted;
  const completeTaskRequiresReview = markAsDonePermissionInfo.requiresReview;

  const deferTaskPermissionInfo = permissionsLoading
    ? { granted: false, requiresReview: false }
    : getPermissionInfo("monthly_checklist", "to_be_done_later");
  const hasDeferTaskPermission = deferTaskPermissionInfo.granted;
  const deferTaskRequiresReview = deferTaskPermissionInfo.requiresReview;

  const closeMonthPermissionInfo = permissionsLoading
    ? { granted: false, requiresReview: false }
    : getPermissionInfo("monthly_checklist", "month_close");
  const hasCloseMonthPermission = closeMonthPermissionInfo.granted;

  // Hooks
  const {
    counts,
    fetchCounts,
    applyStatusTransition: applyCountsStatusTransition,
    loading: countsLoading,
    error: countsError,
  } = useChecklistCounts(selectedMonth);
  const {
    progress,
    fetchProgress,
    applyStatusTransition: applyProgressStatusTransition,
    loading: progressLoading,
    error: progressError,
  } = useChecklistProgress(selectedMonth);
  const {
    items,
    pagination,
    loading: itemsLoading,
    fetchItems,
    loadMore,
    updateItemStatus,
    refresh: refreshItems,
  } = useChecklistItems(
    selectedMonth,
    selectedArea,
    status,
    debouncedItemSearch,
  );

  // Format month name from selectedMonth (YYYY-MM format)
  const formattedMonthName = useMemo(() => {
    if (progress?.month_name) {
      return progress.month_name;
    }
    if (selectedMonth) {
      const [year, month] = selectedMonth.split("-");
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      return date.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    }
    return "Loading...";
  }, [progress?.month_name, selectedMonth]);

  const allAreaCounts = counts?.[MONTH_END_CHECKLIST_ALL_AREAS_KEY];
  const tabCounts = useMemo(
    () => ({
      pending: allAreaCounts?.pending || 0,
      done: allAreaCounts?.done || 0,
      rejected: allAreaCounts?.rejected || 0,
      toBeDoneLater: allAreaCounts?.to_be_done_later || 0,
    }),
    [allAreaCounts],
  );

  // Check if checklist not found (404 error)
  // Only show "not found" if we have an error message containing "not found" and we're not loading
  const isChecklistNotFound = useMemo(() => {
    const hasNotFoundError =
      (progressError && progressError.toLowerCase().includes("not found")) ||
      (countsError && countsError.toLowerCase().includes("not found"));

    // Only show not found message when we're not loading and have the error
    return hasNotFoundError && !progressLoading && !countsLoading;
  }, [progressError, countsError, progressLoading, countsLoading]);

  // Fetch counts and progress when month changes
  useEffect(() => {
    fetchCounts();
    fetchProgress();
  }, [selectedMonth, fetchCounts, fetchProgress]);

  // Fetch items when area or status changes (including initial load)
  useEffect(() => {
    if (selectedArea) {
      fetchItems(0);
      setSelectedItem(null);
    }
  }, [selectedArea, status, fetchItems]);

  // Check if month can be closed (all items must be done, no pending/rejected items)
  const canCloseMonth = useMemo(() => {
    if (!progress) return false;
    // Month is already closed
    if (progress.status === "closed") return false;
    // Check if all items are done
    if (progress.approved_items < progress.total_items) return false;
    // Check if there are any pending review items
    if (progress.pending_review_items > 0) return false;
    // Check if there are any rejected items
    if (progress.rejected_items > 0) return false;
    return true;
  }, [progress]);

  // Check if month is closed
  const isMonthClosed = useMemo(() => {
    return progress?.status === "closed";
  }, [progress]);

  // Handle mark item done
  const handleMarkDone = useCallback(
    async (itemId: number, notes?: string) => {
      if (isMonthClosed) {
        showError("Cannot perform actions on items when the month is closed");
        return;
      }

      if (!hasCompleteTaskPermission) {
        showError("You do not have permission to complete tasks");
        return;
      }

      const item = items.find((i) => i.id === itemId);
      if (!item) return;

      // Store notes for review modal
      setReviewNotes(notes || "");
      setReviewAction("mark_as_done");

      if (completeTaskRequiresReview) {
        // Open review modal
        setReviewItem(item);
        setIsReviewModalOpen(true);
      } else {
        // Directly mark as done - pass description from item
        try {
          const completedAt = new Date().toISOString();
          await markItemDoneUseCase.execute(itemId, notes, item.description);
          showSuccess("Item marked as done successfully");
          updateItemStatus(itemId, "done", {
            notes,
            completed_at: completedAt,
            updated_at: completedAt,
          });
          applyCountsStatusTransition(
            item.accounting_area,
            item.status,
            "done",
          );
          applyProgressStatusTransition(item.status, "done");
          setSelectedItem((currentItem) =>
            currentItem?.id === itemId
              ? {
                  ...currentItem,
                  status: "done",
                  notes,
                  completed_at: completedAt,
                  updated_at: completedAt,
                }
              : currentItem,
          );
        } catch (error: any) {
          showError(error.message || "Failed to mark item as done");
        }
      }
    },
    [
      isMonthClosed,
      hasCompleteTaskPermission,
      completeTaskRequiresReview,
      items,
      updateItemStatus,
      applyCountsStatusTransition,
      applyProgressStatusTransition,
      showSuccess,
      showError,
    ],
  );

  const handleMarkToBeDoneLater = useCallback(
    async (itemId: number) => {
      if (isMonthClosed) {
        showError("Cannot perform actions on items when the month is closed");
        return;
      }
      if (!hasDeferTaskPermission) {
        showError("You do not have permission to update checklist items");
        return;
      }
      const item = items.find((i) => i.id === itemId);
      if (!item) return;

      setReviewNotes("");
      setReviewAction("to_be_done_later");

      if (deferTaskRequiresReview) {
        setReviewItem(item);
        setIsReviewModalOpen(true);
        return;
      }

      try {
        const updatedAt = new Date().toISOString();
        await markItemToBeDoneLaterUseCase.execute(itemId);
        showSuccess("Item marked as to be done later");
        updateItemStatus(itemId, "to_be_done_later", {
          updated_at: updatedAt,
        });
        applyCountsStatusTransition(
          item.accounting_area,
          item.status,
          "to_be_done_later",
        );
        applyProgressStatusTransition(item.status, "to_be_done_later");
        setSelectedItem((currentItem) =>
          currentItem?.id === itemId
            ? {
                ...currentItem,
                status: "to_be_done_later",
                updated_at: updatedAt,
              }
            : currentItem,
        );
      } catch (error: any) {
        showError(error.message || "Failed to update item");
      }
    },
    [
      isMonthClosed,
      hasDeferTaskPermission,
      deferTaskRequiresReview,
      items,
      updateItemStatus,
      applyCountsStatusTransition,
      applyProgressStatusTransition,
      showSuccess,
      showError,
    ],
  );

  // Handle upload attachment
  const handleUploadAttachment = useCallback(
    async (itemId: number, file: File) => {
      if (isMonthClosed) {
        showError("Cannot perform actions on items when the month is closed");
        throw new Error("Month is closed");
      }

      if (!hasCompleteTaskPermission) {
        showError("You do not have permission to upload attachments");
        throw new Error("Permission denied");
      }

      try {
        const attachment = await uploadAttachmentUseCase.execute(itemId, file);
        showSuccess("Attachment uploaded successfully");
        return attachment;
      } catch (error: any) {
        showError(error.message || "Failed to upload attachment");
        throw error;
      }
    },
    [isMonthClosed, hasCompleteTaskPermission, showSuccess, showError],
  );

  // Handle delete attachment
  const handleDeleteAttachment = useCallback(
    async (attachmentId: number) => {
      if (isMonthClosed) {
        showError("Cannot perform actions on items when the month is closed");
        return;
      }

      if (!hasCompleteTaskPermission) {
        showError("You do not have permission to delete attachments");
        return;
      }

      try {
        await deleteAttachmentUseCase.execute(attachmentId);
        showSuccess("Attachment deleted successfully");
        // No refresh needed - ChecklistItemCard handles local state update
      } catch (error: any) {
        showError(error.message || "Failed to delete attachment");
        throw error;
      }
    },
    [isMonthClosed, hasCompleteTaskPermission, showSuccess, showError],
  );

  // Handle close month
  const handleCloseMonth = useCallback(async () => {
    if (!hasCloseMonthPermission) {
      showError("You do not have permission to close the month");
      return;
    }

    if (!progress) {
      showError("Progress data not available");
      return;
    }

    if (!canCloseMonth) {
      showError(
        "All required items must have proof uploaded before closing the month",
      );
      return;
    }

    try {
      await closeMonthUseCase.execute(progress.checklist_id);

      // Trigger CFO cron_client_month to sync client monthly data after close
      const [yearStr, monthStr] = selectedMonth.split("-");
      const year = parseInt(yearStr, 10);
      const month = parseInt(monthStr, 10);
      try {
        await triggerCronClientMonthUseCase.execute(year, month);
      } catch (cronError: any) {
        showError(
          cronError.message ||
            "Month closed but client monthly data sync failed. You may retry from CFO reports.",
        );
      }

      showSuccess("Month closed successfully");
      await fetchCounts();
      await fetchProgress();
      await refreshItems();
    } catch (error: any) {
      showError(error.message || "Failed to close month");
    }
  }, [
    hasCloseMonthPermission,
    progress,
    canCloseMonth,
    selectedMonth,
    fetchCounts,
    fetchProgress,
    refreshItems,
    showSuccess,
    showError,
  ]);

  // Handle review modal success
  const handleReviewModalSuccess = useCallback(async () => {
    const reviewedItem = reviewItem;
    const updatedAt = new Date().toISOString();

    setIsReviewModalOpen(false);
    setReviewItem(null);
    setReviewAction("mark_as_done");
    setReviewNotes("");

    if (reviewedItem) {
      updateItemStatus(reviewedItem.id, "submitted_for_review", {
        notes: reviewNotes || reviewedItem.notes,
        updated_at: updatedAt,
      });
      applyCountsStatusTransition(
        reviewedItem.accounting_area,
        reviewedItem.status,
        "submitted_for_review",
      );
      applyProgressStatusTransition(
        reviewedItem.status,
        "submitted_for_review",
      );
      setSelectedItem((currentItem) =>
        currentItem?.id === reviewedItem.id
          ? {
              ...currentItem,
              status: "submitted_for_review",
              notes: reviewNotes || currentItem.notes,
              updated_at: updatedAt,
            }
          : currentItem,
      );
    }
  }, [
    reviewAction,
    reviewItem,
    reviewNotes,
    updateItemStatus,
    applyCountsStatusTransition,
    applyProgressStatusTransition,
    showSuccess,
  ]);

  // Prepare change details for review modal
  const reviewChangeDetails: ChangeDetails | undefined = useMemo(() => {
    if (!reviewItem) return undefined;

    return {
      raw_json: {
        item_title: reviewItem.title,
        item_description: reviewItem.description,
        accounting_area: reviewItem.accounting_area,
        module: reviewItem.module,
        notes: reviewNotes,
        requested_action: reviewAction,
        requested_status:
          reviewAction === "to_be_done_later" ? "to_be_done_later" : "done",
        attachments:
          reviewAction === "to_be_done_later"
            ? []
            : reviewItem.attachments.map((att) => ({
                id: att.id,
                file_name: att.file_name,
                original_filename: att.original_filename,
                s3_url: att.s3_url,
                uploaded_at: att.uploaded_at,
              })),
        system_info: reviewItem.system_info,
        status: reviewItem.status,
      },
    };
  }, [reviewItem, reviewAction, reviewNotes]);

  // Show loading state while permissions are being fetched
  if (permissionsLoading) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center bg-default-background">
        <Loader />
        <span className="text-body font-body text-subtext-color mt-4">
          Loading permissions...
        </span>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col items-start bg-default-background">
      {/* Header Section */}
      <div className="flex w-full flex-col items-start gap-3 border-b border-solid border-neutral-border bg-default-background px-8 py-4">
        <div className="flex w-full items-start justify-between gap-4">
          <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Month Close — {formattedMonthName}
            </span>
            <span className="text-body font-body text-subtext-color">
              {progress
                ? `${progress.completed_items} of ${progress.total_items} tasks done`
                : progressLoading
                  ? "Loading..."
                  : isChecklistNotFound
                    ? "No checklist found for this month"
                    : "Loading..."}
            </span>
            <div
              className="mt-3 flex w-full max-w-full gap-2 overflow-x-auto pb-1"
              role="tablist"
              aria-label="Choose month"
            >
              {months.map((month) => {
                const isSelected = selectedMonth === month.value;
                return (
                  <button
                    key={month.value}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    aria-label={month.label}
                    onClick={() => {
                      setSelectedMonth(month.value);
                      setSelectedArea(ACCOUNTING_AREAS[0]);
                    }}
                    className={`shrink-0 rounded-md border border-solid px-3 py-1.5 text-body font-body transition-colors ${
                      isSelected
                        ? "border-brand-600 bg-brand-50 text-brand-800"
                        : "border-neutral-border bg-default-background text-default-font hover:bg-neutral-50"
                    }`}
                  >
                    {month.shortLabel}
                  </button>
                );
              })}
            </div>
            {/* Show message if checklist not found */}
            {isChecklistNotFound && !progressLoading && (
              <div className="mt-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-md">
                <span className="text-body font-body text-yellow-800">
                  No checklist has been created for {formattedMonthName}. Please
                  contact an administrator to create a checklist for this month.
                </span>
              </div>
            )}
            {/* Show closed message if month is closed */}
            {progress?.status === "closed" && progress.closed_at && (
              <div className="mt-2 px-4 py-2 bg-green-50 border border-green-200 rounded-md">
                <span className="text-body font-body text-green-800">
                  ✓ Month has been closed
                  {progress.closed_by_user && (
                    <>
                      {" "}
                      by{" "}
                      <span className="font-semibold">
                        {progress.closed_by_user.name ||
                          progress.closed_by_user.email}
                      </span>
                    </>
                  )}
                  {progress.closed_at && (
                    <>
                      {" "}
                      on{" "}
                      <span className="font-semibold">
                        {new Date(progress.closed_at).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </>
                  )}
                </span>
              </div>
            )}
          </div>
          {progress?.status !== "closed" && !isChecklistNotFound && (
            <Button
              disabled={!canCloseMonth || !hasCloseMonthPermission}
              onClick={handleCloseMonth}
              title={
                !canCloseMonth
                  ? progress?.pending_review_items || progress?.rejected_items
                    ? `Cannot close month: ${
                        progress?.pending_review_items || 0
                      } item(s) pending review, ${
                        progress?.rejected_items || 0
                      } item(s) rejected. All items must be done.`
                    : `Cannot close month: ${
                        (progress?.total_items || 0) -
                        (progress?.approved_items || 0)
                      } item(s) not completed (done or to be done later).`
                  : ""
              }
            >
              Close Month
            </Button>
          )}
        </div>
        <div className="flex w-full items-center gap-4">
          <Tabs>
            <Tabs.Item
              active={status === "pending"}
              onClick={() => setStatus("pending")}
            >
              Pending ({tabCounts.pending})
            </Tabs.Item>
            <Tabs.Item
              active={status === "done"}
              onClick={() => setStatus("done")}
            >
              Done ({tabCounts.done})
            </Tabs.Item>
            <Tabs.Item
              active={status === "rejected"}
              onClick={() => setStatus("rejected")}
            >
              Rejected ({tabCounts.rejected})
            </Tabs.Item>
            <Tabs.Item
              active={status === "to_be_done_later"}
              onClick={() => setStatus("to_be_done_later")}
            >
              To be done later ({tabCounts.toBeDoneLater})
            </Tabs.Item>
          </Tabs>
        </div>
      </div>

      {/* Three Column Layout */}
      <div className="flex w-full grow shrink-0 basis-0 items-start overflow-hidden">
        {/* Left Sidebar - Accounting Areas */}
        <AccountingAreaSidebar
          counts={counts}
          selectedArea={selectedArea}
          onAreaChange={setSelectedArea}
          status={status}
        />

        {/* Main Content - Checklist Items */}
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6 self-stretch bg-neutral-50 px-6 py-6 overflow-auto">
          {isChecklistNotFound ? (
            <div className="flex w-full flex-col items-center justify-center py-16">
              <div className="text-center max-w-md">
                <div className="mb-4 text-6xl">📋</div>
                <span className="text-heading-2 font-heading-2 text-default-font block mb-2">
                  No Checklist Found
                </span>
                <span className="text-body font-body text-subtext-color">
                  No checklist has been created for {formattedMonthName}. Please
                  contact an administrator to create a checklist for this month.
                </span>
              </div>
            </div>
          ) : selectedArea ? (
            <>
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <span className="text-heading-1 font-heading-1 text-default-font shrink-0">
                  {selectedArea === MONTH_END_CHECKLIST_ALL_AREAS_KEY
                    ? "All accounting areas"
                    : selectedArea}
                </span>
                <TextField
                  label="Search by title"
                  className="w-full min-w-0 max-w-md"
                >
                  <TextField.Input
                    value={itemSearchInput}
                    onChange={(e) => setItemSearchInput(e.target.value)}
                    placeholder="Type to filter tasks…"
                    aria-label="Search checklist items by title"
                  />
                </TextField>
              </div>
              <div className="flex w-full grow shrink-0 basis-0 items-start gap-6">
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4">
                  {itemsLoading && items.length === 0 ? (
                    <div className="flex w-full items-center justify-center py-8">
                      <Loader />
                    </div>
                  ) : items.length > 0 ? (
                    <>
                      {items.map((item) => {
                        const commonProps = {
                          item,
                          onViewTaskGuidance: () => setSelectedItem(item),
                          onMarkDone: handleMarkDone,
                          onMarkToBeDoneLater:
                            hasDeferTaskPermission && !isMonthClosed
                              ? handleMarkToBeDoneLater
                              : undefined,
                          onUploadAttachment: handleUploadAttachment,
                          onDeleteAttachment: handleDeleteAttachment,
                          hasPermission:
                            hasCompleteTaskPermission && !isMonthClosed,
                          requiresReview: completeTaskRequiresReview,
                          loading: itemsLoading,
                          disabled: isMonthClosed,
                        };

                        if (
                          item.status === "approved" ||
                          item.status === "done" ||
                          item.status === "to_be_done_later"
                        ) {
                          return (
                            <ApprovedChecklistItemCard
                              key={item.id}
                              {...commonProps}
                            />
                          );
                        }

                        if (item.status === "rejected") {
                          return (
                            <SentBackChecklistItemCard
                              key={item.id}
                              {...commonProps}
                            />
                          );
                        }

                        return (
                          <PendingChecklistItemCard
                            key={item.id}
                            {...commonProps}
                          />
                        );
                      })}
                      {pagination?.hasMore && (
                        <div className="flex w-full items-center justify-center py-4 pb-8">
                          <Button
                            variant="neutral-secondary"
                            onClick={loadMore}
                            disabled={itemsLoading}
                          >
                            {itemsLoading ? "Loading..." : "Load More"}
                          </Button>
                        </div>
                      )}
                      {/* Bottom padding spacer to ensure last card is fully visible */}
                      <div className="h-24 w-full flex-shrink-0" />
                    </>
                  ) : (
                    <div className="flex w-full items-center justify-center py-8">
                      <span className="text-body font-body text-subtext-color">
                        {debouncedItemSearch
                          ? "No items match your search"
                          : selectedArea === MONTH_END_CHECKLIST_ALL_AREAS_KEY
                            ? "No items found for this month"
                            : "No items found for this accounting area"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex w-full items-center justify-center py-8">
              <span className="text-body font-body text-subtext-color">
                Choose an accounting area to view checklist items
              </span>
            </div>
          )}
        </div>

        {/* Right Sidebar - Task Guidance */}
        {selectedItem && (
          <TaskGuidancePanel
            item={selectedItem}
            onClose={() => setSelectedItem(null)}
          />
        )}
      </div>

      {/* Review Request Modal */}
      {reviewItem && (
        <ReviewRequestModal
          open={isReviewModalOpen}
          onOpenChange={setIsReviewModalOpen}
          moduleCode="monthly_checklist"
          actionCode={reviewAction}
          entityType="month_end_checklist_item"
          entityId={reviewItem.id}
          changeDetails={reviewChangeDetails}
          onSuccess={handleReviewModalSuccess}
          skipNavigation={true}
        />
      )}
    </div>
  );
};
